import { z } from "zod";
import { MAX_ORG_SCAN_OBJECTS } from "./org-scan.js";
export const API_VERSION = "2026-07-15";
export const PRODUCT_VERSION = "1.7.0";
export const MESSAGE_PROTOCOL_VERSION = 1;
export const MAX_SYNC_QUERY_RECORDS = 2_000;
export const MAX_SKILL_PICKLIST_VALUES = 500;
export const MAX_LEGACY_SKILL_PICKLIST_VALUES = 5_000;
export const MAX_SKILL_TEST_CASES = 500;
export const salesforceContextSchema = z.object({
    orgId: z.string().min(15).max(18),
    userId: z.string().min(15).max(18),
    recordId: z.string().min(15).max(18).optional(),
    recordLabel: z.string().min(1).max(240).optional(),
    objectApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/).optional(),
    accountId: z.string().min(15).max(18).optional(),
    accountLabel: z.string().min(1).max(240).optional(),
    contactId: z.string().min(15).max(18).optional(),
    contactLabel: z.string().min(1).max(240).optional(),
    opportunityId: z.string().min(15).max(18).optional(),
    opportunityLabel: z.string().min(1).max(240).optional(),
    leadId: z.string().min(15).max(18).optional(),
    leadLabel: z.string().min(1).max(240).optional(),
    visitorEmail: z.string().email().max(320).optional(),
    userDisplayName: z.string().min(1).max(160).optional(),
    userAvatarUrl: z.string().url().max(2048).refine((value) => {
        try {
            return new URL(value).protocol === "https:";
        }
        catch {
            return false;
        }
    }, {
        message: "Salesforce 用户头像必须使用 HTTPS"
    }).optional(),
    appName: z.string().max(120).optional()
});
export const actionOperationSchema = z.enum([
    "query",
    "aggregate",
    "create",
    "update",
    "delete"
]);
/**
 * Business-safe write boundary for every object supported by the assistant.
 * Salesforce FLS is still checked at execution time; this policy prevents a
 * field from another module (or a technically present but redundant field)
 * from reaching the preview in the first place.
 */
export const actionWritableFieldsByObject = {
    Account: [
        "Name", "Type", "ParentId", "Phone", "Fax", "Website", "Industry", "NumberOfEmployees", "AnnualRevenue",
        "BillingStreet", "BillingCity", "BillingState", "BillingPostalCode", "BillingCountry",
        "ShippingStreet", "ShippingCity", "ShippingState", "ShippingPostalCode", "ShippingCountry", "Description"
    ],
    Contact: [
        "Salutation", "FirstName", "LastName", "AccountId", "Title", "Department", "Phone", "MobilePhone", "Email",
        "MailingStreet", "MailingCity", "MailingState", "MailingPostalCode", "MailingCountry", "Description"
    ],
    Lead: [
        "FirstName", "LastName", "Company", "Phone", "MobilePhone", "Email", "Status", "LeadSource", "Country", "Description",
        "Project_Name__c", "Project_Location__c", "Project_Power__c", "Energy_storage_capacity_AC_side_kWh__c",
        "Application_Scenario__c", "Expected_delivery_time__c", "Destination_Country__c", "Destination_Region__c",
        "Scope_of_supply__c", "Bidding_Type__c", "CP_Required__c", "EPC__c"
    ],
    Opportunity: [
        "Name", "AccountId", "StageName", "CloseDate", "Amount", "Probability", "Description", "Primary_Contact__c",
        "Client_Name__c", "Project_Location__c", "Project_Power__c", "Power_Rating_MW__c", "Storage_Capacity_MWh__c",
        "Energy_storage_capacity_AC_side_kWh__c", "Application_Scenario__c", "Expected_Tender_Window__c",
        "Expected_Grid_Connection__c", "Expected_delivery_time__c", "Project_Risk__c", "Competitor_Info__c",
        "Decision_Factors__c", "Solution_Scope__c", "Scope_of_supply__c", "Tender_Status__c", "Bidding_Type__c",
        "Destination_Country__c", "Destination_Region__c", "PCS_EMS_Requirement__c"
    ],
    Sales_Visit__c: [
        "Account__c", "Contact__c", "Lead__c", "Opportunity__c", "Visit_Time__c", "Visit_Channel__c", "Visit_Type__c",
        "Region__c", "Country_Area__c", "Topic__c", "Customer_Request__c", "Pain_Point__c", "Product_Interest__c",
        "Competitor_Info__c", "Visit_Summary__c", "Next_Action__c", "Source_Channel__c", "Confidence__c", "Raw_Input__c"
    ],
    AI_Quote_Request__c: [
        "Account__c", "Contact__c", "Lead__c", "Opportunity__c", "Client_Name__c", "Project_Name__c", "Quotation_Type__c",
        "Quote_Status__c", "Currency__c", "Trade_Terms__c", "Payment_Terms__c", "Quotation_Deadline__c",
        "Expected_Delivery_Date__c", "Expected_delivery_time__c", "Project_Power__c", "Energy_storage_capacity_AC_side_kWh__c",
        "Application_Scenario__c", "Scope_of_supply__c", "Missing_Info__c", "Quote_Summary__c", "Approval_Status__c",
        "Approval_Stage__c", "Remarks__c", "Raw_Input__c", "Confidence__c"
    ],
    Case: [
        "SuppliedName", "SuppliedEmail", "SuppliedPhone", "SuppliedCompany", "Subject", "Description", "Origin", "Status",
        "Priority", "Reason", "AccountId", "ContactId", "Type"
    ],
    Task: ["Subject", "Status", "Priority", "ActivityDate", "WhoId", "WhatId", "Description", "OwnerId"]
};
export const actionRequiredCreateFieldsByObject = {
    Account: ["Name"],
    Contact: ["LastName"],
    Lead: ["LastName", "Company"],
    Opportunity: ["Name", "StageName", "CloseDate"],
    Task: ["Subject"],
    Case: ["Subject", "Origin"]
};
const actionWritableFieldSets = new Map(Object.entries(actionWritableFieldsByObject).map(([objectApiName, fields]) => [objectApiName, new Set(fields)]));
export function isActionWritableField(objectApiName, fieldApiName) {
    return actionWritableFieldSets.get(objectApiName)?.has(fieldApiName) ?? false;
}
export const actionFilterSchema = z.object({
    field: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    operator: z.enum(["eq", "ne", "lt", "lte", "gt", "gte", "like", "in"]),
    value: z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.array(z.union([z.string(), z.number(), z.boolean()])).max(50)
    ])
});
export const actionFieldChangeSchema = z.object({
    fieldApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    label: z.string().min(1).max(120),
    oldValue: z.unknown().optional(),
    newValue: z.unknown().optional()
});
const actionBatchPrimitiveSchema = z.union([z.string(), z.number(), z.boolean()]);
export const actionBatchTransformSchema = z.object({
    fieldApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    operation: z.literal("add_months"),
    amount: z.number().int().min(-120).max(120).refine((value) => value !== 0, "月份调整量不能为 0"),
    monthEndPolicy: z.literal("last_day").default("last_day")
});
/**
 * A batch request keeps the original source values in their original order.
 * Repeated values are intentional evidence from the user's attachment and
 * must not be silently removed by the planner.
 */
export const actionBatchUpdateSchema = z.object({
    matchField: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    requestedValues: z.array(actionBatchPrimitiveSchema).min(1).max(50),
    transforms: z.array(actionBatchTransformSchema).min(1).max(10),
    preserveDuplicates: z.literal(true).default(true)
});
export const actionBatchRecordSchema = z.object({
    recordId: z.string().min(15).max(18),
    recordLabel: z.string().min(1).max(240).optional(),
    lastModifiedDate: z.string().datetime(),
    sourceValue: actionBatchPrimitiveSchema,
    sourceOccurrences: z.array(z.number().int().min(1).max(50)).min(1).max(50),
    fields: z.record(z.string(), z.unknown()),
    updates: z.record(z.string(), z.unknown()),
    fieldChanges: z.array(actionFieldChangeSchema).min(1).max(20),
    excluded: z.boolean().default(false)
});
export const actionBatchCreateItemSchema = z.object({
    sourceIndex: z.number().int().min(1).max(500),
    sourceLabel: z.string().min(1).max(240).optional(),
    fields: z.record(z.string(), z.unknown()),
    fieldChanges: z.array(actionFieldChangeSchema).min(1).max(50),
    excluded: z.boolean().default(false),
    warnings: z.array(z.string().min(1).max(500)).max(10).default([])
});
export const actionBatchCreateSchema = z.object({
    items: z.array(actionBatchCreateItemSchema).min(2).max(50),
    preserveDuplicates: z.literal(true).default(true)
});
export const actionMetricSchema = z.object({
    function: z.enum(["count", "sum", "avg", "min", "max"]),
    field: z.preprocess((value) => value === null ? undefined : value, z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/).optional()),
    alias: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,39}$/),
    label: z.string().min(1).max(120)
}).superRefine((metric, context) => {
    if (metric.function !== "count" && !metric.field) {
        context.addIssue({ code: "custom", message: "聚合字段不能为空", path: ["field"] });
    }
});
export const actionOrderBySchema = z.object({
    field: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    direction: z.enum(["asc", "desc"])
});
export const actionDateRangeSchema = z.object({
    field: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    relative: z.preprocess((value) => value === null ? undefined : value, z.enum(["today", "this_week", "this_month", "this_quarter", "this_year", "last_7_days", "last_30_days"]).optional()),
    start: z.preprocess((value) => value === null ? undefined : value, z.string().date().optional()),
    end: z.preprocess((value) => value === null ? undefined : value, z.string().date().optional())
}).superRefine((range, context) => {
    if (!range.relative && !range.start && !range.end) {
        context.addIssue({ code: "custom", message: "日期范围不能为空" });
    }
});
export const actionPresentationSchema = z.object({
    preferredView: z.enum(["auto", "table", "kpi", "funnel", "bar", "line", "donut"]).default("auto")
});
export const actionQueryScopeSchema = z.enum([
    "current_user_access",
    "current_user_owned",
    "current_record",
    "explicit_filters"
]);
export const actionSymbolicBindingSchema = z.object({
    fieldApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    sourceActionIndex: z.number().int().min(1).max(10),
    sourceValue: z.literal("record_id"),
    label: z.string().min(1).max(240)
});
export const actionPlanSchema = z.object({
    skillKey: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,79}$/),
    operation: actionOperationSchema,
    objectApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    recordId: z.preprocess((value) => value === null ? undefined : value, z.string().min(15).max(18).optional()),
    fields: z.record(z.string(), z.unknown()).default({}),
    selectFields: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(30).default([]),
    omittedDisplayFields: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(30).optional(),
    filters: z.array(actionFilterSchema).max(12).default([]),
    limit: z.number().int().min(1).max(MAX_SYNC_QUERY_RECORDS).default(10),
    summary: z.string().min(1).max(500),
    recordLabel: z.preprocess((value) => value === null ? undefined : value, z.string().min(1).max(240).optional()),
    fieldChanges: z.array(actionFieldChangeSchema).max(50).default([]),
    metrics: z.preprocess((value) => value === null ? undefined : value, z.array(actionMetricSchema).max(10).optional()),
    groupBy: z.preprocess((value) => value === null ? undefined : value, z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(3).optional()),
    orderBy: z.preprocess((value) => value === null ? undefined : value, z.array(actionOrderBySchema).max(3).optional()),
    dateRange: z.preprocess((value) => value === null ? undefined : value, actionDateRangeSchema.optional()),
    resultShape: z.preprocess((value) => value === null ? undefined : value, z.enum(["records", "metrics", "records_and_metrics"]).optional()),
    queryScope: z.preprocess((value) => value === null ? undefined : value, actionQueryScopeSchema.optional()),
    presentation: z.preprocess((value) => value === null ? undefined : value, actionPresentationSchema.optional()),
    expectedLastModifiedDate: z.preprocess((value) => value === null ? undefined : value, z.string().datetime().optional()),
    batchUpdate: z.preprocess((value) => value === null ? undefined : value, actionBatchUpdateSchema.optional()),
    batchRecords: z.preprocess((value) => value === null ? undefined : value, z.array(actionBatchRecordSchema).max(50).optional()),
    batchCreate: z.preprocess((value) => value === null ? undefined : value, actionBatchCreateSchema.optional()),
    symbolicBindings: z.preprocess((value) => value === null ? undefined : value, z.array(actionSymbolicBindingSchema).max(10).optional()),
    destructiveIntent: z.boolean().default(false)
}).superRefine((plan, context) => {
    const addOperationIssue = (message, path) => context.addIssue({
        code: "custom",
        message,
        path: [path]
    });
    const hasWritePayload = Object.keys(plan.fields).length > 0 || plan.fieldChanges.length > 0 ||
        Boolean(plan.batchUpdate || plan.batchRecords?.length || plan.batchCreate || plan.symbolicBindings?.length);
    if (plan.operation !== "query" && plan.operation !== "aggregate" && plan.limit > 50) {
        addOperationIssue("创建、更新和删除的单次记录边界仍为 50 条", "limit");
    }
    if ((plan.batchUpdate || plan.batchRecords?.length) && plan.operation !== "update") {
        addOperationIssue("批量记录仅支持更新操作", "operation");
    }
    if (plan.batchRecords?.length && !plan.batchUpdate) {
        addOperationIssue("批量预览缺少原始批量请求", "batchUpdate");
    }
    if (plan.batchCreate && plan.operation !== "create") {
        addOperationIssue("批量新建仅支持创建操作", "operation");
    }
    if ((plan.operation === "query" || plan.operation === "aggregate") && hasWritePayload) {
        addOperationIssue("查询和统计计划不得包含写入载荷", "fields");
    }
    if ((plan.operation === "query" || plan.operation === "aggregate") && (plan.recordId || plan.expectedLastModifiedDate)) {
        addOperationIssue("查询和统计计划不得携带写入记录版本", "recordId");
    }
    if (plan.operation !== "delete" && plan.destructiveIntent) {
        addOperationIssue("只有删除计划可以声明破坏性意图", "destructiveIntent");
    }
    if (plan.operation === "delete") {
        if (!plan.destructiveIntent)
            addOperationIssue("删除计划必须明确声明破坏性意图", "destructiveIntent");
        if (!plan.recordId)
            addOperationIssue("删除计划必须锁定一条记录", "recordId");
        if (plan.limit !== 1)
            addOperationIssue("删除计划只能包含一条记录", "limit");
        if (hasWritePayload)
            addOperationIssue("删除计划不得包含字段或批量写入载荷", "fields");
    }
});
export const actionPreviewResultSchema = z.object({
    recordId: z.string().min(15).max(18).optional(),
    recordLabel: z.string().min(1).max(240).optional(),
    lastModifiedDate: z.string().datetime().optional(),
    fields: z.record(z.string(), z.unknown()).optional(),
    nullFields: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(50).optional(),
    records: z.array(actionBatchRecordSchema).max(50).optional(),
    unmatchedValues: z.array(actionBatchPrimitiveSchema).max(50).optional()
}).superRefine((preview, context) => {
    const hasSingle = Boolean(preview.recordId && preview.lastModifiedDate && preview.fields);
    const hasBatch = Boolean(preview.records?.length);
    if (hasSingle === hasBatch) {
        context.addIssue({ code: "custom", message: "预览必须包含一条记录或一组批量记录" });
    }
});
export const actionStateSchema = z.enum([
    "draft",
    "pending_confirmation",
    "needs_attention",
    "executing",
    "succeeded",
    "failed",
    "cancelled",
    "expired"
]);
export const actionRecordSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    groupId: z.string().uuid().optional(),
    groupSequence: z.number().int().min(1).max(10).optional(),
    groupSize: z.number().int().min(2).max(10).optional(),
    dependencies: z.array(z.object({
        fieldApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
        sourceActionId: z.string().uuid(),
        sourceValue: z.literal("record_id"),
        label: z.string().min(1).max(240),
        resolvedRecordId: z.string().min(15).max(18).optional()
    })).max(10).optional(),
    groupApprovedAt: z.string().datetime().optional(),
    plan: actionPlanSchema,
    state: actionStateSchema,
    contentHash: z.string().length(64),
    version: z.number().int().min(1),
    confirmationsRequired: z.number().int().min(0).max(2),
    confirmationsReceived: z.number().int().min(0).max(2),
    scenarioId: z.string().uuid().optional(),
    scenarioVersion: z.string().min(1).max(40).optional(),
    idempotencyKey: z.string().length(64).optional(),
    executionClaimedAt: z.string().datetime().optional(),
    executionCompletedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    lastChangeSummary: z.string().max(500).optional(),
    result: z.unknown().optional(),
    error: z.string().max(1000).optional()
});
export const draftCommandSchema = z.object({
    type: z.enum(["revise", "confirm", "cancel"]),
    actionId: z.string().uuid().nullish().transform((value) => value ?? undefined),
    setFields: z.record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/), z.unknown()).default({}),
    removeFields: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(50).default([]),
    batchEdits: z.array(z.object({
        sourceIndex: z.number().int().min(1).max(500),
        recordId: z.string().min(15).max(18).optional(),
        setFields: z.record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/), z.unknown()).default({}),
        removeFields: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(50).default([]),
        excluded: z.boolean().optional()
    })).max(50).optional(),
    summary: z.string().min(1).max(500)
});
export const messageSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    role: z.enum(["user", "assistant", "system"]),
    content: z.string().max(100_000),
    createdAt: z.string().datetime(),
    actionId: z.string().uuid().optional()
});
export const conversationSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    ownerSalesforceUserId: z.string().min(15).max(18),
    channel: z.enum(["salesforce", "platform_web", "website", "development"]),
    title: z.string().min(1).max(160),
    status: z.enum(["in_progress", "pending_confirmation", "needs_attention", "completed", "archived"]),
    pendingItemCount: z.number().int().nonnegative().max(500).default(0),
    context: salesforceContextSchema.partial(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    lastMessageAt: z.string().datetime().optional(),
    archivedAt: z.string().datetime().optional()
});
export const conversationPageSchema = z.object({
    items: z.array(conversationSchema),
    nextCursor: z.string().min(1).max(240).optional(),
    hasMore: z.boolean()
});
export const messagePageSchema = z.object({
    items: z.array(messageSchema),
    nextCursor: z.string().min(1).max(240).optional(),
    hasMore: z.boolean()
});
export const conversationTaskStateSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    ownerSalesforceUserId: z.string().min(15).max(18),
    businessGoal: z.string().max(1000).default(""),
    relatedObjects: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/)).max(10).default([]),
    relatedRecordIds: z.array(z.string().min(15).max(18)).max(50).default([]),
    confirmedFacts: z.record(z.string(), z.unknown()).default({}),
    candidateRecords: z.array(z.record(z.string(), z.unknown())).max(50).default([]),
    unresolvedQuestions: z.array(z.string().min(1).max(500)).max(20).default([]),
    pendingActionIds: z.array(z.string().uuid()).max(50).default([]),
    attachmentInsightIds: z.array(z.string().uuid()).max(3).default([]),
    executionResults: z.array(z.object({
        actionId: z.string().uuid(),
        recordId: z.string().min(15).max(18).optional(),
        recordUrl: z.string().url().optional(),
        status: z.enum(["succeeded", "failed", "partial", "cancelled"])
    })).max(100).default([]),
    updatedAt: z.string().datetime()
});
export const conversationSummarySchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    ownerSalesforceUserId: z.string().min(15).max(18),
    throughMessageId: z.string().uuid(),
    messageCount: z.number().int().min(1),
    summary: z.string().min(1).max(8000),
    createdAt: z.string().datetime()
});
export const conversationRecordReferenceSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    ownerSalesforceUserId: z.string().min(15).max(18),
    objectApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
    recordId: z.string().min(15).max(18),
    recordLabel: z.string().min(1).max(240).optional(),
    source: z.enum(["page_context", "user_selected", "action_result"]),
    createdAt: z.string().datetime()
});
export const attachmentInsightSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    ownerSalesforceUserId: z.string().min(15).max(18),
    attachmentId: z.string().uuid(),
    fileName: z.string().min(1).max(240),
    mimeType: z.string().min(1).max(120),
    summary: z.string().max(8000).default(""),
    extractedFields: z.record(z.string(), z.unknown()).default({}),
    warnings: z.array(z.string().min(1).max(500)).max(50).default([]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const userPreferenceSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    ownerSalesforceUserId: z.string().min(15).max(18),
    language: z.string().min(2).max(20).optional(),
    timeZone: z.string().min(1).max(80).optional(),
    dateFormat: z.string().min(1).max(40).optional(),
    unitSystem: z.string().min(1).max(40).optional(),
    updatedAt: z.string().datetime()
});
const skillApiKeySchema = z.string().regex(/^[a-z][a-z0-9_-]{1,79}$/);
const skillFieldApiNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/);
export const skillTopicSchema = z.object({
    key: skillApiKeySchema,
    name: z.string().min(1).max(120),
    businessGoal: z.string().min(1).max(1000),
    triggerExamples: z.array(z.string().min(1).max(500)).min(1).max(20)
});
export const skillInstructionSchema = z.object({
    key: skillApiKeySchema,
    text: z.string().min(1).max(2000),
    priority: z.number().int().min(1).max(100)
});
export const skillActionDefinitionSchema = z.object({
    key: skillApiKeySchema,
    name: z.string().min(1).max(120),
    description: z.string().min(1).max(1000),
    objectApiName: skillFieldApiNameSchema,
    objectScope: z.enum(["fixed", "allowed_objects"]).default("fixed"),
    operation: actionOperationSchema,
    requiredFields: z.array(skillFieldApiNameSchema).max(100),
    readableFields: z.array(skillFieldApiNameSchema).max(100),
    writableFields: z.array(skillFieldApiNameSchema).max(100),
    confirmation: z.enum(["none", "required"]),
    executionMode: z.literal("deterministic_program")
});
export const skillVariableSchema = z.object({
    key: skillApiKeySchema,
    label: z.string().min(1).max(160),
    dataType: z.enum(["string", "number", "date", "datetime", "boolean", "record_id", "record_list"]),
    required: z.boolean(),
    source: z.enum(["conversation", "page_context", "attachment", "salesforce"])
});
export const skillPolicySchema = z.object({
    zeroResultStrategy: z.literal("explicit_not_found"),
    multipleMatchStrategy: z.literal("ask_user_to_choose"),
    duplicateStrategy: z.literal("preserve_and_report"),
    writeConfirmation: z.literal("preview_then_confirm"),
    maxBatchSize: z.number().int().min(1).max(50),
    partialFailureStrategy: z.literal("keep_success_and_retry_failed"),
    contextStrategy: z.literal("record_page_only_unless_user_specifies")
});
export const skillResponseTemplatesSchema = z.object({
    notFound: z.string().min(1).max(1000),
    multipleMatches: z.string().min(1).max(1000),
    previewReady: z.string().min(1).max(1000),
    success: z.string().min(1).max(1000),
    partialFailure: z.string().min(1).max(1000)
});
export const skillQualityGates = [
    "query",
    "aggregate",
    "write_preview",
    "cross_object_sequence",
    "permission_denied",
    "unopened_object",
    "ambiguity",
    "required_fields",
    "validation_rule",
    "cancellation",
    "duplicate",
    "planning_timeout",
    "invalid_model_output"
];
export const skillQualityGateSchema = z.enum(skillQualityGates);
export const skillTestCaseV2Schema = z.object({
    key: skillApiKeySchema,
    name: z.string().min(1).max(160),
    qualityGate: skillQualityGateSchema.optional(),
    targetObjectApiName: skillFieldApiNameSchema.optional(),
    turns: z.array(z.object({
        role: z.enum(["user", "assistant"]),
        content: z.string().min(1).max(4000)
    })).min(1).max(20),
    expectedOutcome: z.enum(["action_sequence", "no_action", "planning_error", "planning_timeout"]).optional(),
    expectedActionSequence: z.array(z.object({
        operation: actionOperationSchema,
        objectApiName: skillFieldApiNameSchema
    })).max(50)
}).superRefine((testCase, context) => {
    const outcome = testCase.expectedOutcome ?? (testCase.expectedActionSequence.length ? "action_sequence" : "no_action");
    if (outcome === "action_sequence" && !testCase.expectedActionSequence.length) {
        context.addIssue({ code: "custom", path: ["expectedActionSequence"], message: "动作序列测试至少需要一个预期 Action" });
    }
    if (outcome !== "action_sequence" && testCase.expectedActionSequence.length) {
        context.addIssue({ code: "custom", path: ["expectedActionSequence"], message: "无动作或错误测试不能包含预期 Action" });
    }
});
export const skillTestSuiteSchema = z.object({
    datasetId: z.string().uuid().optional(),
    datasetVersion: z.string().regex(/^\d+\.\d+\.\d+$/),
    cases: z.array(skillTestCaseV2Schema).min(1).max(MAX_SKILL_TEST_CASES)
});
export const testDatasetSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    skillKey: skillApiKeySchema,
    name: z.string().min(1).max(160),
    status: z.enum(["active", "archived"]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const testDatasetVersionSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    datasetId: z.string().uuid(),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    cases: z.array(skillTestCaseV2Schema).min(1).max(MAX_SKILL_TEST_CASES),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime()
});
export const platformSystemStatusSchema = z.object({
    organizationId: z.string().min(15).max(18),
    environment: z.enum(["development", "test", "production"]),
    runtimeVersion: z.string().min(1).max(40),
    regionProfile: z.enum(["global", "china"]),
    persistence: z.object({
        kind: z.string().min(1).max(40),
        productionReady: z.boolean()
    }),
    attachmentStorage: z.object({
        kind: z.enum(["memory", "oss", "s3"]),
        productionReady: z.boolean()
    }),
    credentialVault: z.object({ configured: z.boolean() }),
    userAccess: z.object({
        activeOrdinaryUsers: z.number().int().nonnegative(),
        readyOrdinaryUsers: z.number().int().nonnegative()
    }),
    platformEmailAuth: z.object({
        enabled: z.boolean(),
        deliveryMode: z.enum(["smtp", "console"])
    }),
    salesforce: z.object({
        cloudVariant: z.enum(["global", "alibaba_cloud", "unverified"]),
        regionCompatible: z.boolean(),
        ticketVerificationConfigured: z.boolean(),
        userAccessOAuthConfigured: z.boolean(),
        orgManagementConfigured: z.boolean(),
        orgManagementMode: z.string().min(1).max(80),
        orgIntegrationConfigured: z.boolean(),
        orgIntegrationMode: z.string().min(1).max(80)
    }),
    modelGateway: z.object({
        mode: z.string().min(1).max(80),
        configured: z.boolean()
    }),
    guardrails: z.object({
        writePreviewRequired: z.literal(true),
        maxAttachmentBytes: z.number().int().positive(),
        maxTotalAttachmentBytes: z.number().int().positive(),
        maxConversationAttachments: z.number().int().positive(),
        maxBatchSize: z.number().int().positive(),
        recentContextTurns: z.number().int().positive(),
        contextSummaryMessageThreshold: z.number().int().positive(),
        integrationReadOnly: z.literal(true)
    }),
    evaluatedAt: z.string().datetime()
});
export const skillSchemaDependencySchema = z.object({
    snapshotId: z.string().uuid(),
    schemaHash: z.string().length(64),
    objects: z.array(z.object({
        objectApiName: skillFieldApiNameSchema,
        label: z.string().min(1).max(160).optional(),
        fields: z.array(z.object({
            apiName: skillFieldApiNameSchema,
            label: z.string().min(1).max(160).optional(),
            type: z.string().min(1).max(80),
            // Historical V1.5 snapshots may contain the full Salesforce value set.
            // New builders compact this to MAX_SKILL_PICKLIST_VALUES, while the
            // larger parsing boundary keeps existing customer scans readable.
            picklistValues: z.array(z.string().max(255)).max(MAX_LEGACY_SKILL_PICKLIST_VALUES),
            picklistValueCount: z.number().int().nonnegative().optional(),
            picklistValuesHash: z.string().length(64).optional(),
            picklistValuesTruncated: z.boolean().optional()
        })).max(200),
        permissionSetApiNames: z.array(skillFieldApiNameSchema).max(50),
        requiredFields: z.array(skillFieldApiNameSchema).max(200).optional(),
        layoutRequiredFields: z.array(skillFieldApiNameSchema).max(200).optional(),
        validationRuleFields: z.array(skillFieldApiNameSchema).max(200).optional(),
        createPreviewFields: z.array(skillFieldApiNameSchema).max(200).optional(),
        recordTypes: z.array(z.string().min(1).max(240)).max(500).optional(),
        validationRules: z.array(z.string().min(1).max(240)).max(200),
        duplicateRules: z.array(z.string().min(1).max(240)).max(100)
    })).min(1).max(50)
});
export const skillModulesSchema = z.object({
    sourceCandidateId: z.string().uuid().optional(),
    topic: skillTopicSchema,
    instructions: z.array(skillInstructionSchema).min(1).max(100),
    // Managed scenarios normally use fixed actions. The system baseline uses a
    // small set of parameterized actions across administrator-selected objects.
    actions: z.array(skillActionDefinitionSchema).min(1).max(30),
    variables: z.array(skillVariableSchema).max(100),
    policy: skillPolicySchema,
    responseTemplates: skillResponseTemplatesSchema,
    testSuite: skillTestSuiteSchema,
    schemaDependency: skillSchemaDependencySchema
});
export const skillManifestSchema = z.object({
    key: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,79}$/),
    name: z.string().min(1).max(120),
    description: z.string().max(2000),
    version: z.string().regex(/^\d+\.\d+\.\d+(?:-[a-z0-9.-]+)?$/),
    status: z.enum(["draft", "validating", "testing", "trial_ready", "trial", "published", "archived"]),
    instructions: z.string().min(1).max(20_000),
    allowedObjects: z.array(z.object({
        objectApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/),
        operations: z.array(actionOperationSchema).min(1),
        readableFields: z.array(z.string()).max(100),
        writableFields: z.array(z.string()).max(100)
    })).min(1),
    testCases: z.array(z.object({
        name: z.string().min(1).max(160),
        input: z.string().min(1).max(4000),
        expectedOperation: actionOperationSchema,
        expectedObject: z.string()
    })).max(100),
    modules: skillModulesSchema.optional()
});
/**
 * Reports whether a versioned test suite defines every gate implied by the
 * compiled capability. Definition coverage is intentionally separate from a
 * passing TestRun: a listed gate is not release evidence until it executes.
 */
export function assessSkillQualityGateDefinitions(skill) {
    if (!skill.modules)
        return { required: [], defined: [], missing: [], missingCases: [], complete: false };
    const requirements = [
        { gate: "unopened_object" },
        { gate: "ambiguity" },
        { gate: "planning_timeout" },
        { gate: "invalid_model_output" }
    ];
    const actionTargets = skill.modules.actions.flatMap((action) => {
        const objectApiNames = (action.objectScope ?? "fixed") === "allowed_objects"
            ? skill.allowedObjects
                .filter(({ operations }) => operations.includes(action.operation))
                .map(({ objectApiName }) => objectApiName)
            : [action.objectApiName];
        return objectApiNames.map((objectApiName) => ({ operation: action.operation, objectApiName }));
    });
    for (const target of actionTargets) {
        requirements.push({
            gate: target.operation === "query" || target.operation === "aggregate" ? target.operation : "write_preview",
            objectApiName: target.objectApiName,
            operation: target.operation
        });
    }
    const writeTargets = actionTargets.filter(({ operation }) => operation === "create" || operation === "update" || operation === "delete");
    const writeObjectApiNames = [...new Set(writeTargets.map(({ objectApiName }) => objectApiName))];
    for (const objectApiName of skill.allowedObjects.map(({ objectApiName }) => objectApiName)) {
        requirements.push({ gate: "permission_denied", objectApiName });
    }
    for (const objectApiName of writeObjectApiNames)
        requirements.push({ gate: "cancellation", objectApiName });
    const dependencyByObject = new Map(skill.modules.schemaDependency.objects.map((object) => [object.objectApiName, object]));
    for (const objectApiName of writeObjectApiNames) {
        const dependency = dependencyByObject.get(objectApiName);
        const operations = new Set(writeTargets.filter((target) => target.objectApiName === objectApiName).map(({ operation }) => operation));
        if (operations.has("create") && dependency?.requiredFields?.length)
            requirements.push({ gate: "required_fields", objectApiName });
        if ((operations.has("create") || operations.has("update")) && dependency?.validationRules.length)
            requirements.push({ gate: "validation_rule", objectApiName });
        if ((operations.has("create") || operations.has("update")) && dependency?.duplicateRules.length)
            requirements.push({ gate: "duplicate", objectApiName });
    }
    if (new Set(skill.allowedObjects.map(({ objectApiName }) => objectApiName)).size > 1)
        requirements.push({ gate: "cross_object_sequence" });
    const uniqueRequirements = [...new Map(requirements.map((requirement) => [
            `${requirement.gate}:${requirement.objectApiName ?? "*"}:${requirement.operation ?? "*"}`,
            requirement
        ])).values()];
    const targetCountsByGate = new Map();
    for (const requirement of uniqueRequirements) {
        if (requirement.objectApiName)
            targetCountsByGate.set(requirement.gate, (targetCountsByGate.get(requirement.gate) ?? 0) + 1);
    }
    const missingCases = uniqueRequirements.filter((requirement) => !skill.modules.testSuite.cases.some((testCase) => {
        if (testCase.qualityGate !== requirement.gate)
            return false;
        if (requirement.gate === "cross_object_sequence") {
            return new Set(testCase.expectedActionSequence.map(({ objectApiName }) => objectApiName)).size > 1;
        }
        if (requirement.operation) {
            return testCase.expectedActionSequence.length === 1 && testCase.expectedActionSequence.some(({ operation, objectApiName }) => operation === requirement.operation && objectApiName === requirement.objectApiName);
        }
        if (requirement.objectApiName) {
            return testCase.targetObjectApiName === requirement.objectApiName ||
                (!testCase.targetObjectApiName && targetCountsByGate.get(requirement.gate) === 1);
        }
        return true;
    }));
    const requiredSet = new Set(uniqueRequirements.map(({ gate }) => gate));
    const missingSet = new Set(missingCases.map(({ gate }) => gate));
    const required = skillQualityGates.filter((gate) => requiredSet.has(gate));
    const defined = required.filter((gate) => !missingSet.has(gate));
    const missing = required.filter((gate) => missingSet.has(gate));
    return { required, defined, missing, missingCases, complete: missingCases.length === 0 };
}
export const businessScenarioCapabilitySchema = z.object({
    objectApiName: skillFieldApiNameSchema,
    operations: z.array(z.enum(["query", "aggregate", "create", "update", "delete"])).min(1).max(5)
});
export const objectCapabilitySummarySchema = z.object({
    objectApiName: skillFieldApiNameSchema,
    label: z.string().min(1).max(240),
    operations: z.array(z.enum(["query", "aggregate", "create", "update", "delete"])).max(5),
    sourceSkillKey: skillApiKeySchema.optional(),
    sourceSkillVersion: z.string().max(40).optional(),
    sourceStatus: skillManifestSchema.shape.status.optional(),
    ready: z.boolean(),
    blockers: z.array(z.string().min(1).max(500)).max(20).default([])
});
export const businessScenarioSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    key: skillApiKeySchema,
    name: z.string().min(1).max(120),
    businessGoal: z.string().min(1).max(1000),
    exampleRequests: z.array(z.string().min(1).max(500)).min(1).max(12),
    capabilities: z.array(businessScenarioCapabilitySchema).min(1).max(MAX_ORG_SCAN_OBJECTS),
    confirmationPolicy: z.literal("preview_then_confirm"),
    linkedSkillKey: skillApiKeySchema,
    linkedSkillVersion: z.string().regex(/^\d+\.\d+\.\d+(?:-[a-z0-9.-]+)?$/),
    assignmentRoleKeys: z.array(z.string().regex(/^[a-z][a-z0-9_-]{1,79}$/)).max(50).default([]),
    status: skillManifestSchema.shape.status,
    sourceSnapshotId: z.string().uuid(),
    readiness: z.object({
        ready: z.boolean(),
        blockers: z.array(z.string().min(1).max(500)).max(50)
    }),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
const bridgeEnvelopeBase = z.object({
    version: z.literal(MESSAGE_PROTOCOL_VERSION),
    requestId: z.string().uuid(),
    nonce: z.string().min(16).max(200)
});
export const bridgeMessageSchema = z.discriminatedUnion("type", [
    bridgeEnvelopeBase.extend({ type: z.literal("agent.ready") }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.session.ticket"),
        ticket: z.string().min(40)
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.context.changed"),
        context: salesforceContextSchema.partial()
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.action.request"),
        actionId: z.string().uuid(),
        plan: actionPlanSchema
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.action.preview.request"),
        actionId: z.string().uuid(),
        plan: actionPlanSchema
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.action.preview.result"),
        actionId: z.string().uuid(),
        ok: z.boolean(),
        result: actionPreviewResultSchema.optional(),
        error: z.string().max(1000).optional()
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("salesforce.action.result"),
        actionId: z.string().uuid(),
        ok: z.boolean(),
        result: z.unknown().optional(),
        error: z.string().max(1000).optional()
    }),
    bridgeEnvelopeBase.extend({ type: z.literal("agent.session.expired") }),
    bridgeEnvelopeBase.extend({ type: z.literal("agent.open.fullscreen") }),
    bridgeEnvelopeBase.extend({
        type: z.literal("agent.panel.resize"),
        preset: z.enum(["compact", "standard", "wide"])
    }),
    bridgeEnvelopeBase.extend({
        type: z.literal("agent.unread.changed"),
        unreadCount: z.number().int().min(0).max(999)
    })
]);
export const confirmationStepsFor = (operation) => {
    if (operation === "delete")
        return 2;
    if (operation === "create" || operation === "update")
        return 1;
    return 0;
};
export * from "./platform.js";
export * from "./v1-2.js";
export * from "./operations.js";
export * from "./org-scan.js";
